import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, USER_TIERS } from '../contexts/AuthContext';
import { Card, Table, Pagination, TierBadge, Button, ConfirmModal } from '../components/common';
import UserActivityModal from '../components/UserActivityModal';

const Users = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAllUsers, updateUserTier, deleteUser, getPendingUsers, user } = useAuth();

  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [providerFilter, setProviderFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showRealName, setShowRealName] = useState({});  // 실명 보기 토글 상태
  const [activityModal, setActivityModal] = useState({ open: false, user: null });  // 활동 로그 모달

  const itemsPerPage = 15;

  // URL 파라미터에서 초기 필터 로드
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter === 'pending') {
      setTierFilter('pending');
    }
  }, [searchParams]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [allUsers, pending] = await Promise.all([
        getAllUsers(),
        getPendingUsers()
      ]);
      setUsers(allUsers.filter(u => u.status === 'approved' || !u.status));
      setPendingUsers(pending);
    } catch (err) {
      setMessage({ text: t('admin.error.loadFailed'), type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [getAllUsers, getPendingUsers, t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleTierChange = async (userId, newTier) => {
    try {
      await updateUserTier(userId, newTier);
      setMessage({ text: t('admin.success.tierUpdated'), type: 'success' });
      await loadUsers();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (err) {
      setMessage({ text: t('admin.error.updateFailed'), type: 'error' });
    }
  };

  const handleDeleteClick = (targetUser) => {
    setDeleteModal({ open: true, user: targetUser });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.user) return;

    setDeleteLoading(true);
    try {
      await deleteUser(deleteModal.user.id);
      setMessage({ text: t('admin.success.deleted'), type: 'success' });
      setDeleteModal({ open: false, user: null });
      await loadUsers();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (err) {
      setMessage({
        text: err.message === 'Cannot delete yourself'
          ? t('admin.error.cannotDeleteSelf')
          : t('admin.error.deleteFailed'),
        type: 'error'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const getTierDisplayName = (tier) => {
    const tierMap = {
      guest: t('auth.tiers.guest'),
      general: t('auth.tiers.general'),
      subscriber: t('auth.tiers.subscriber'),
      family: t('auth.tiers.family'),
      admin: t('auth.tiers.admin')
    };
    return tierMap[tier] || tier;
  };

  const getProviderInfo = (socialProvider) => {
    if (socialProvider === 'google') {
      return { name: 'Google', icon: '🔵', className: 'bg-blue-50 text-blue-600' };
    } else if (socialProvider === 'kakao') {
      return { name: 'Kakao', icon: '🟡', className: 'bg-yellow-50 text-yellow-900' };
    } else {
      return { name: '이메일', icon: '✉️', className: 'bg-gray-100 text-gray-500' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // 실명 보기 토글
  const toggleRealName = (userId) => {
    setShowRealName(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // 필터링된 사용자 목록
  const filteredUsers = users.filter(u => {
    // 검색 필터
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = u.name?.toLowerCase().includes(query);
      const matchesNickname = u.nickname?.toLowerCase().includes(query);
      const matchesEmail = u.email?.toLowerCase().includes(query);
      if (!matchesName && !matchesNickname && !matchesEmail) return false;
    }

    // 가입방법 필터
    if (providerFilter !== 'all') {
      if (providerFilter === 'google' && u.social_provider !== 'google') return false;
      if (providerFilter === 'kakao' && u.social_provider !== 'kakao') return false;
      if (providerFilter === 'email' && u.social_provider) return false;
    }

    // 등급 필터
    if (tierFilter !== 'all' && tierFilter !== 'pending') {
      if (u.tier !== tierFilter) return false;
    }

    return true;
  });

  // 페이지네이션
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 통계
  const stats = {
    total: users.length,
    pending: pendingUsers.length,
    family: users.filter(u => u.tier === 'family').length,
    admin: users.filter(u => u.tier === 'admin').length
  };

  const columns = [
    {
      key: 'provider',
      label: '가입방법',
      width: '100px',
      render: (_, row) => {
        const providerInfo = getProviderInfo(row.social_provider);
        return (
          <span className={`inline-flex items-center gap-1 py-1 px-2 rounded-md text-xs font-medium whitespace-nowrap ${providerInfo.className}`}>
            {providerInfo.icon} {providerInfo.name}
          </span>
        );
      }
    },
    {
      key: 'name',
      label: '닉네임/실명',
      render: (value, row) => {
        const displayName = row.nickname || value;
        const hasNickname = !!row.nickname;
        const isRealNameVisible = showRealName[row.id];

        return (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-gray-900">{displayName}</span>
              {hasNickname && (
                <button
                  className="bg-transparent border-none p-0.5 text-sm cursor-pointer opacity-50 transition-opacity rounded hover:opacity-100 hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRealName(row.id);
                  }}
                  title={isRealNameVisible ? '실명 숨기기' : '실명 보기'}
                >
                  {isRealNameVisible ? '👁️' : '👁️‍🗨️'}
                </button>
              )}
              {row.id === user.id && <span className="inline-flex items-center py-0.5 px-1.5 bg-blue-100 text-blue-700 text-[10px] font-semibold rounded">{t('admin.you')}</span>}
            </div>
            {isRealNameVisible && hasNickname && (
              <span className="block text-[11px] text-gray-400 mt-0.5 py-0.5 px-1.5 bg-gray-100 rounded">실명: {value}</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'email',
      label: t('admin.table.email')
    },
    {
      key: 'tier',
      label: t('admin.table.tier'),
      width: '140px',
      render: (value, row) => (
        <select
          value={value}
          onChange={(e) => handleTierChange(row.id, e.target.value)}
          className="py-1.5 px-2.5 bg-white border border-gray-200 rounded-md text-xs text-gray-900 cursor-pointer transition-all hover:border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
        >
          {Object.values(USER_TIERS).map(tier => (
            <option key={tier} value={tier}>{getTierDisplayName(tier)}</option>
          ))}
        </select>
      )
    },
    {
      key: 'join_date',
      label: '가입일',
      width: '100px',
      render: (value) => formatDate(value) || '-'
    },
    {
      key: 'last_login',
      label: '마지막 접속',
      width: '100px',
      render: (value) => formatDate(value)
    },
    {
      key: 'actions',
      label: t('admin.table.actions'),
      width: '140px',
      align: 'center',
      render: (_, row) => (
        <div className="flex gap-1 justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setActivityModal({ open: true, user: row });
            }}
            className="!text-blue-500 hover:!bg-blue-100 hover:!text-blue-600"
            title="활동 로그 보기"
          >
            활동
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
            disabled={row.id === user.id}
            className="!text-red-500 hover:!bg-red-100 hover:!text-red-600 disabled:!text-gray-300"
          >
            삭제
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6 max-w-[1400px] mx-auto max-md:gap-4">
      {/* 메시지 */}
      {message.text && (
        <div className={`py-3 px-4 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
          {message.text}
        </div>
      )}

      {/* 헤더 */}
      <div className="flex justify-between items-start max-md:flex-col max-md:gap-4">
        <div>
          <h1 className="text-[28px] font-bold text-gray-900 m-0 mb-1 max-md:text-2xl">회원 관리</h1>
          <p className="text-sm text-gray-600 m-0">총 {stats.total}명의 회원이 있습니다</p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate('/signup')}
        >
          + 새 계정 생성
        </Button>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-md:gap-3 max-sm:grid-cols-1">
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${tierFilter === 'all' ? 'bg-blue-500 border-blue-600' : 'bg-white border-gray-200'}`}
          onClick={() => {
            setTierFilter('all');
            setCurrentPage(1);
          }}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${tierFilter === 'all' ? 'grayscale brightness-[10]' : ''}`}>👥</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${tierFilter === 'all' ? 'text-white' : 'text-gray-900'}`}>{stats.total}</span>
            <span className={`text-[13px] ${tierFilter === 'all' ? 'text-white' : 'text-gray-600'}`}>{t('admin.stats.totalUsers')}</span>
          </div>
        </div>
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${tierFilter === 'pending' ? 'bg-amber-500 border-amber-600' : 'bg-white border-gray-200'}`}
          onClick={() => navigate('/users/pending')}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${tierFilter === 'pending' ? 'grayscale brightness-[10]' : ''}`}>⏳</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${tierFilter === 'pending' ? 'text-white' : stats.pending > 0 ? 'text-amber-500' : 'text-gray-900'}`}>{stats.pending}</span>
            <span className={`text-[13px] ${tierFilter === 'pending' ? 'text-white' : 'text-gray-600'}`}>{t('admin.stats.pendingUsers')}</span>
          </div>
        </div>
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${tierFilter === 'family' ? 'bg-blue-500 border-blue-600' : 'bg-white border-gray-200'}`}
          onClick={() => {
            setTierFilter('family');
            setCurrentPage(1);
          }}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${tierFilter === 'family' ? 'grayscale brightness-[10]' : ''}`}>👨‍👩‍👧‍👦</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${tierFilter === 'family' ? 'text-white' : 'text-gray-900'}`}>{stats.family}</span>
            <span className={`text-[13px] ${tierFilter === 'family' ? 'text-white' : 'text-gray-600'}`}>{t('admin.stats.familyMembers')}</span>
          </div>
        </div>
        <div
          className={`flex items-center gap-4 p-5 border rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md max-md:p-4 max-md:gap-3 ${tierFilter === 'admin' ? 'bg-blue-500 border-blue-600' : 'bg-white border-gray-200'}`}
          onClick={() => {
            setTierFilter('admin');
            setCurrentPage(1);
          }}
        >
          <span className={`text-[28px] leading-none flex-shrink-0 max-md:text-2xl ${tierFilter === 'admin' ? 'grayscale brightness-[10]' : ''}`}>👑</span>
          <div className="flex flex-col gap-0.5">
            <span className={`text-2xl font-bold leading-tight max-md:text-xl ${tierFilter === 'admin' ? 'text-white' : 'text-gray-900'}`}>{stats.admin}</span>
            <span className={`text-[13px] ${tierFilter === 'admin' ? 'text-white' : 'text-gray-600'}`}>{t('admin.stats.admins')}</span>
          </div>
        </div>
      </div>

      {/* 필터 영역 */}
      <Card className="bg-white">
        <div className="flex flex-col gap-4">
          {/* 검색 */}
          <div className="max-w-[400px] max-md:max-w-full">
            <input
              type="text"
              placeholder="이름 또는 이메일로 검색..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full py-2.5 px-3.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 transition-all focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10 placeholder:text-gray-400"
            />
          </div>

          {/* 가입방법 필터 */}
          <div className="flex items-center gap-3 flex-wrap max-md:flex-col max-md:items-start max-md:gap-2">
            <span className="text-[13px] font-medium text-gray-600 whitespace-nowrap">가입방법:</span>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: '전체', count: users.length },
                { key: 'google', label: '🔵 Google', count: users.filter(u => u.social_provider === 'google').length },
                { key: 'kakao', label: '🟡 Kakao', count: users.filter(u => u.social_provider === 'kakao').length },
                { key: 'email', label: '✉️ 이메일', count: users.filter(u => !u.social_provider).length }
              ].map(filter => (
                <button
                  key={filter.key}
                  className={`py-1.5 px-3 rounded-md text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${providerFilter === filter.key ? 'bg-blue-500 border border-blue-600 text-white' : 'bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 hover:border-gray-300'}`}
                  onClick={() => {
                    setProviderFilter(filter.key);
                    setCurrentPage(1);
                  }}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>
          </div>

          {/* 등급 필터 */}
          <div className="flex items-center gap-3 flex-wrap max-md:flex-col max-md:items-start max-md:gap-2">
            <span className="text-[13px] font-medium text-gray-600 whitespace-nowrap">회원등급:</span>
            <div className="flex flex-wrap gap-2">
              <button
                className={`py-1.5 px-3 rounded-md text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${tierFilter === 'all' ? 'bg-blue-500 border border-blue-600 text-white' : 'bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 hover:border-gray-300'}`}
                onClick={() => {
                  setTierFilter('all');
                  setCurrentPage(1);
                }}
              >
                전체 ({users.length})
              </button>
              {Object.values(USER_TIERS).map(tier => (
                <button
                  key={tier}
                  className={`py-1.5 px-3 rounded-md text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${tierFilter === tier ? 'bg-blue-500 border border-blue-600 text-white' : 'bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 hover:border-gray-300'}`}
                  onClick={() => {
                    setTierFilter(tier);
                    setCurrentPage(1);
                  }}
                >
                  {getTierDisplayName(tier)} ({users.filter(u => u.tier === tier).length})
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* 테이블 */}
      <Card noPadding className="bg-white overflow-hidden max-md:overflow-x-auto">
        <Table
          columns={columns}
          data={paginatedUsers}
          loading={loading}
          emptyMessage="해당 조건의 회원이 없습니다."
          hoverable
        />
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </Card>

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, user: null })}
        onConfirm={handleDeleteConfirm}
        title="회원 삭제"
        message={`정말로 "${deleteModal.user?.name}" 회원을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`}
        confirmText="삭제"
        cancelText="취소"
        variant="danger"
        loading={deleteLoading}
      />

      {/* 활동 로그 모달 */}
      <UserActivityModal
        user={activityModal.user}
        isOpen={activityModal.open}
        onClose={() => setActivityModal({ open: false, user: null })}
      />
    </div>
  );
};

export default Users;
